/* eslint-disable max-lines */
/* eslint-disable react-hooks/exhaustive-deps */
import React, { useEffect, useRef, useState } from "react";
import { setCookie } from "nookies";
import { shallowEqual, useDispatch, useSelector } from "react-redux";
import { useParams } from "next/navigation";
import {
  checkIsInternationalProduct,
  getAssocProdIdPrice,
  getPrice,
  isNonDeliverable,
  isNonDeliverableAbroad,
  onAutoFillDateTimeSlots,
  switchDeliveryType,
} from "../../../utils/pdp/nonDeliverable";
import PropTypes from "prop-types";
import { constants, productTypes } from "../../../constants/pageConstants/pdpConstants";
import CustomImage from "../../../components/atoms/customImage";
import Toast from "../../../components/atoms/toast";
import { requestToFetchDeliveryDateAction } from "../../../store/slices/earliestDeliveryDatesSlice";
import {
  resetGetDeliveryDateAndGeoPriceAction,
  setDeliveryDetailsAction,
} from "../../../store/slices/common/shippingDateAndSlotSelection";
import { cleverTapCommonEvents } from "../../../analytics/clevertapConstants";
import useAnalytics from "../../../analytics/useAnalytics";
import { COUNTRIES } from "../../../constants/common/common";
import { getEarliestDeliveryDateString } from "../../../utils/common/deliveryDate";
import { LAST_VISITED_PINCODE } from "../../../constants/storage";
import CookieUtil from "../../../utils/storage/cookieUtil";

/**
 * This method is used to handle same sku products with delivery type selection
 *
 * @returns {React.ReactElement} jsx for to choose delivery type for the same sku products
 */
function ChooseDeliveryType({ commonCleverTapData }) {
  const {
    isMobile,
    catalogId,
    deliveryType,
    pincode,
    product,
    selectedCurrency,
    deliveryDates,
    shippingDetails,
    isLoading,
    earliestDeliveryDays,
    shippingDetailsInternational,
    deliveryDetailsData,
    nextAvailableDifference,
    selectedIntLocation,
  } = useSelector(
    (state) => ({
      isMobile: state.appConfig.isMobile,
      catalogId: state.appConfig.catalogId,
      deliveryType: state?.pdp?.deliveryType,
      pincode: state?.locationLock?.selectedDomesticLocation?.pincode,
      product: state?.pdp?.clientCurrentClickedItem,
      selectedCurrency: state?.currencyList?.userSelectedCurrency?.currency,
      deliveryDates: state?.shippingDateAndSlotSelection?.deliveryDates,
      shippingDetails: state?.shippingDateAndSlotSelection?.shippingDetails,
      isLoading: state?.shippingDateAndSlotSelection?.loading,
      earliestDeliveryDays: state?.deliveryDate?.details?.deliveryDates,
      shippingDetailsInternational: state?.shippingDateAndSlotSelection?.internationalDeliveryMethods,
      nextAvailableDifference: state?.shippingDateAndSlotSelection?.nextAvailableDifference,
      selectedIntLocation: state?.locationLock.selectedIntLocation,
    }),
    shallowEqual,
  );
  const dispatch = useDispatch();
  const { trackEvent } = useAnalytics();
  const isInitialMount = useRef(true);
  const lastDispatchedPincode = useRef(null);

  const params = useParams();
  const [courier, setCourier] = useState({});
  const [express, setExpress] = useState({});
  const [showToast, setShowToast] = useState(false);
  const { primaryCategoryId, productId, assocProductDetails, urlIdentifier, productStockAvailable } = product;
  const isInternational = checkIsInternationalProduct(primaryCategoryId);
  const cityOrPincode = !isInternational ? pincode : selectedIntLocation?.pincode;
  const [isNotServiceable, setIsNotServiceable] = useState(false);
  const isExpressNotAvailable = earliestDeliveryDays?.[express?.productId] === "_NA_";
  const isCourierNotAvailable = earliestDeliveryDays?.[courier?.productId] === "_NA_";
  const pincodeFromCookie = CookieUtil.getCookie(LAST_VISITED_PINCODE);

  const isCourierDelivery = deliveryType === productTypes.COURIER || deliveryType === productTypes.PERSONALIZED;
  const isExpressDelivery = deliveryType === productTypes.EXPRESS;
  const isDateOrdinal = true;
  const commonDeliverySelect = {
    ...commonCleverTapData,
    date_of_delivery: deliveryDetailsData?.selectedDate,
    next_available_date_difference: catalogId !== COUNTRIES.INDIA ? "" : nextAvailableDifference?.nextAvailableDateDiff,
    delivery_shipping: deliveryDetailsData?.currentShippingMethod?.shippingMethodName || "",
    delivery_shipping_fee: deliveryDetailsData?.timeSlots?.timeSlots?.shippingPrice ?? 0,
    delivery_slot: deliveryDetailsData?.timeSlots?.formatTime || "",
    earliest_delivery_date: deliveryDates?.deliveryDates?.[0] ?? "",
  };

  useEffect(() => {
    if (cityOrPincode && !isLoading) {
      const noService = !isInternational
        ? isNonDeliverable(deliveryDates, !!shippingDetails?.data?.length, assocProductDetails, primaryCategoryId)
        : isNonDeliverableAbroad(shippingDetailsInternational?.isShippingDetailsExists);
      setIsNotServiceable(noService);
    }
  }, [deliveryDates, shippingDetailsInternational, cityOrPincode, isLoading]);

  /**
   * Get Product IDs to Fetch Delivery Dates of express, courier.
   */
  const getProductIdsToFetchDeliveryDates = () => {
    let productIds = [];
    const courierType = deliveryType === productTypes.PERSONALIZED ? productTypes.PERSONALIZED : productTypes.COURIER;
    const expressInfo = getAssocProdIdPrice(productTypes.EXPRESS, product);
    const courierInfo = getAssocProdIdPrice(courierType, product);

    if (expressInfo?.productId && courierInfo?.productId) {
      setExpress(expressInfo);
      setCourier(courierInfo);
      productIds = [expressInfo.productId, courierInfo.productId];
    }

    return productIds;
  };

  useEffect(() => {
    const productIds = getProductIdsToFetchDeliveryDates();

    if (productIds.length > 0) {
      const currentPincode = pincode || pincodeFromCookie || "";

      const shouldDispatch =
        (!pincode && !pincodeFromCookie && lastDispatchedPincode.current === null) ||
        currentPincode !== lastDispatchedPincode.current;

      if (shouldDispatch) {
        lastDispatchedPincode.current = currentPincode;
        dispatch(
          requestToFetchDeliveryDateAction({
            productIds: productIds.toString(","),
            pincode: currentPincode,
            isChooseDeliveryType: true,
          }),
        );
      }
    }
  }, [pincode, pincodeFromCookie]);

  useEffect(() => {
    const { slugs } = params;
    if (urlIdentifier && slugs[0]) {
      setCookie({}, slugs[0], urlIdentifier);
    }
  }, [params, urlIdentifier]);

  useEffect(() => {
    if (isInitialMount.current) {
      isInitialMount.current = false;
    } else if (isExpressNotAvailable && !isCourierNotAvailable) {
      switchDeliveryType(
        productStockAvailable,
        courier.deliveryType,
        courier.productId,
        productId,
        primaryCategoryId,
        catalogId,
        pincode,
        dispatch,
      );
      setShowToast(true);
    } else if (isCourierNotAvailable && !isExpressNotAvailable) {
      switchDeliveryType(
        productStockAvailable,
        express.deliveryType,
        express.productId,
        productId,
        primaryCategoryId,
        catalogId,
        pincode,
        dispatch,
      );
      setShowToast(true);
    }
  }, [isExpressNotAvailable, isCourierNotAvailable]);

  useEffect(() => {
    if (shippingDetails?.data?.length > 0 && pincode) {
      onAutoFillDateTimeSlots(shippingDetails, deliveryDates, dispatch);
    } else {
      dispatch(setDeliveryDetailsAction({}));
      dispatch(resetGetDeliveryDateAndGeoPriceAction());
    }
  }, [shippingDetails?.data, pincode]);

  /**
   * Get the Express and Courier Styles.
   * @param {*} isNotAvailable
   * @param {*} isAvailableSelected
   * @returns
   */
  const getExpressCourierStyle = (isNotAvailable, isAvailableDeliverySelected) =>
    isNotAvailable
      ? "opacity-50 border-radioUncheck"
      : isAvailableDeliverySelected
        ? "opacity-100 bg-fnp-100 border-darkOliveGreen"
        : "opacity-100 border-radioUncheck";
  /**
   * Handle switching to courier delivery type
   */
  const onHandleCourierSwitch = () => {
    if (!isExpressNotAvailable && !isCourierDelivery) {
      switchDeliveryType(
        productStockAvailable,
        productTypes.COURIER,
        courier.productId,
        productId,
        primaryCategoryId,
        catalogId,
        pincode,
        dispatch,
      );
    }
    trackEvent(cleverTapCommonEvents.deliveryTypeSelected, commonDeliverySelect, ["clevertap"]);
  };

  /**
   * Handle switching to express delivery type
   */
  const onHandleExpressSwitch = () => {
    if (!isExpressDelivery) {
      switchDeliveryType(
        productStockAvailable,
        productTypes.EXPRESS,
        express.productId,
        productId,
        primaryCategoryId,
        catalogId,
        pincode,
        dispatch,
      );
    }
    trackEvent(cleverTapCommonEvents.deliveryTypeSelected, commonDeliverySelect, ["clevertap"]);
  };

  /**
   * Get Earliest Delivery Day Text.
   * @param {*} expressOrCourier
   * @returns
   */
  const getDeliveryDay = (expressOrCourier, isDeliveryType) => (
    <div className={`text-12 font-400 leading-20  ${isDeliveryType ? "text-fnp-400" : "text-grey-500"}`}>
      {earliestDeliveryDays[expressOrCourier.productId] === "_NA_"
        ? constants.NOT_AVAILABLE
        : `${constants.EARLIEST_BY} ${getEarliestDeliveryDateString(
            earliestDeliveryDays[expressOrCourier.productId],
            isDateOrdinal,
          )}`}
    </div>
  );

  return (
    <>
      <div className="flex gap-8">
        <button
          id="express-switch"
          onClick={onHandleExpressSwitch}
          className={`w-full rounded-[30px] border-[1.5px]  py-8 ${getExpressCourierStyle(isExpressNotAvailable, isExpressDelivery)}`}
          disabled={isExpressNotAvailable}
        >
          <div className="flex flex-col">
            <div
              className={`inline-flex justify-center gap-[2px] text-12 font-600 leading-20 ${isExpressDelivery ? "text-fnp-400" : "text-black-100"} `}
            >
              <h3>Hand Delivery</h3>
              <span className="price">
                {express.price && (
                  <>
                    {selectedCurrency?.currencyCode && <span>{`${selectedCurrency?.currencyCode} `}</span>}
                    <span>{getPrice(express.price, selectedCurrency)}</span>
                  </>
                )}
              </span>
            </div>
            {earliestDeliveryDays?.[express.productId] &&
              express?.productId &&
              getDeliveryDay(express, isExpressDelivery)}
          </div>
        </button>
        <button
          id="courier-switch"
          onClick={onHandleCourierSwitch}
          className={`w-full rounded-[30px] border-[1.5px]  py-8 ${getExpressCourierStyle(isCourierNotAvailable, isCourierDelivery)}`}
          disabled={isCourierNotAvailable}
        >
          <div className="flex flex-col">
            <div
              className={`inline-flex justify-center gap-[2px] text-12 font-600 leading-20 ${isCourierDelivery ? "text-fnp-400" : "text-black-100"} `}
            >
              <h3>{constants.COURIER_DELIVERY}</h3>
              <span className="price">
                {courier.price && (
                  <>
                    {selectedCurrency?.currencyCode && <span>{`${selectedCurrency?.currencyCode} `}</span>}
                    <span>{getPrice(courier.price, selectedCurrency)}</span>
                  </>
                )}
              </span>
            </div>
            {earliestDeliveryDays?.[courier.productId] &&
              courier?.productId &&
              getDeliveryDay(courier, isCourierDelivery)}
          </div>
        </button>
        {showToast &&
          (isCourierNotAvailable || isExpressNotAvailable) &&
          !isNotServiceable &&
          !(isCourierNotAvailable && isExpressNotAvailable) && (
            <Toast
              variant="success"
              isToast={showToast}
              header={isCourierNotAvailable ? constants.COURIER_NOT_AVAILABLE : constants.HAND_NOT_AVAILABLE}
              position="fixed bottom-80"
              duration={3000}
              // eslint-disable-next-line react/jsx-handler-names
              onToastChange={(value) => setShowToast(value)}
            />
          )}
      </div>
      {isMobile && !isNotServiceable && !isExpressNotAvailable && isCourierDelivery && (
        <div className="flex gap-4 rounded-4 bg-blue-200/[.06] p-4">
          <CustomImage
            src="/icons/info-circle-blue.svg"
            width={16}
            height={16}
            alt="delivery info"
            sizes="10vw"
            isPriority={false}
          />
          <span className="text-12 font-500 leading-16 text-grey-500">{constants.FAST_DELIVERY_AVAILABLE}</span>
        </div>
      )}
    </>
  );
}
ChooseDeliveryType.propTypes = {
  commonCleverTapData: PropTypes.object,
};
export default ChooseDeliveryType;
